import * as Classes from '../common/classes';
import * as React from 'react';
import { Navbar, NAVBAR_DEFAULT_WIDTH } from './Navbar';

import { AppMenuProps, NavMenu } from './NavMenu';
import classNames from 'classnames';
import { UserMenuProps } from './UserMenu';
import { BaseProps } from '../common/props';
import { TestEnvironmentBar } from '../test-environment-bar/TestEnvironmentBar';
import { useAppConfig } from '@mc/react-app-base';
import {
  SidePanelItem,
  SidePanelItemActionEnum,
  SidePanelProps,
} from '../app/AppLayout';
import { BadgeCount } from '../badge-count/BadgeCount';
import { useMemo } from 'react';
import { NavbarButton } from '../button/NavbarButton';
import { isMobile } from '../common/utils';
import { SidePanelContentRenderer } from './SidePanelContentRenderer';
import { Modal } from '../modal/Modal';
import { Dialog } from '../dialog/Dialog';
import { DialogBody } from '../dialog/DialogBody';
import { DialogFooter } from '../dialog/DialogFooter';
import { Button } from '../button/Button';

/** Minimální šířka viewportu (px), při které zůstane menu připnuté. Pod touto hodnotou se automaticky skryje. */
const NAVBAR_MENU_PINUP_MIN_WIDTH = 700;

/** Vrátí true, pokud je viewport v minimalizovaném módu (ne mobilní, ale pod minimální šířkou pro připnutí). */
const checkIsMinimizedMode = () =>
  !isMobile && window.innerWidth < NAVBAR_MENU_PINUP_MIN_WIDTH;

export type NavBarAppMenuProps = AppMenuProps;

export type NavBarProps = {
  /*
   * V základu false, pokud nastaveno jako true, navbar automaticky zobrazí a připne appMenu, dokud si to uživatel neodepne ručně.
   * Pokud je ale počet položek v menu 0, tak se i když je true se panel nezobrazí.
   */
  defaultVisibleMenu?: boolean;
  brand?: React.ReactNode;
  icon?: React.ReactNode;
  center?: React.ReactNode;
  appMenu?: NavBarAppMenuProps;
  userMenu?: UserMenuProps;
  navbarMenuActions?: React.ReactNode;
};

export type NavBarMenuLayoutProps = BaseProps & {
  sidePanel?: SidePanelProps;
} & {
  navbar?: NavBarProps;
};

//asi trochu hack, ale dělá to co chci
export const NavBarMenuLayout: React.FC<NavBarMenuLayoutProps> = ({
  children,
  className,
  style,
  navbar,
  sidePanel,
}) => {
  const appConfig = useAppConfig();
  const getDefaultState = () => {
    const pinned = localStorage.getItem('pinnedPanel');
    if (pinned !== null) {
      return Boolean(JSON.parse(pinned || 'false'));
    } else {
      return navbar?.defaultVisibleMenu;
    }
  };
  //@ts-ignore
  const isUnderEl = (parentEl, el) => {
    while (el !== null) {
      if (el === parentEl) {
        return true;
      }
      el = el.parentNode;
    }
    return false;
  };
  const popUpRef = React.useRef(null);
  const sidePanelButtonsRef = React.useRef(null);
  const [pinned, setPinned] = React.useState(getDefaultState());
  const [isMinimizedMode, setIsMinimizedMode] = React.useState(checkIsMinimizedMode);
  /** Stav pinnedPanel těsně před přechodem do minimalizovaného módu – pro obnovení při zvětšení okna. */
  const pinnedBeforeMinimize = React.useRef<boolean | undefined>(undefined);
  /** Sleduje, zda jsme naposledy byli pod minimální šířkou. */
  const wasUnderMinWidth = React.useRef<boolean>(checkIsMinimizedMode());
  const togglePin = () => {
    localStorage.setItem('pinnedPanel', JSON.stringify(!pinned));
    setPinned(!pinned);
  };
  const [selectedSidePanelItemKey, setSelectedSidePanelItemKey] =
    React.useState<string | undefined>();

  const renderItemContent = (
    item: SidePanelItem,
    closeCallback: () => void
  ) => {
    const content = item.content;
    if (typeof content === 'function') {
      return content(closeCallback);
    }
    return content;
  };

  const handleSidePanelItemChange = (item: SidePanelItem) => {
    // Na mobilu zobrazíme Modal.show přímo v handleru (ne v useEffect/rendereru)
    if (isMobile) {
      Modal.show(({ onClose: modalOnClose }) => (
        <Dialog title={item.label} outsideClickClose onClose={modalOnClose} size={"fullscreen"}>
          <DialogBody>{renderItemContent(item, modalOnClose)}</DialogBody>
          <DialogFooter>
            <Button style={'primary'} onClick={modalOnClose}>
              Zavřít
            </Button>
          </DialogFooter>
        </Dialog>
      ));
      return;
    }

    // Desktop — toggle stavu
    if (selectedSidePanelItemKey === item.key) {
      setSelectedSidePanelItemKey(undefined);
    } else {
      setSelectedSidePanelItemKey(item.key);
    }
  };

  const selectedSidePanelItem = useMemo(() => {
    if (!selectedSidePanelItemKey) {
      return undefined;
    }
    return sidePanel?.items.find(
      (item) => item.key === selectedSidePanelItemKey
    );
  }, [selectedSidePanelItemKey, sidePanel?.items]);

  const renderSelectedItemContent = () => {
    if (!selectedSidePanelItem?.content) {
      return null;
    }
    /*HACK jako prase, ale kvůli zpětné kompabilitě a "multi" využití ténto komponenty je potřeba podporovat react komponentu
     * tak i funkci co přejímá parametr close a renderuje react komponentu
     */
    const content = selectedSidePanelItem.content;
    if (typeof content === 'function') {
      return content(() => setSelectedSidePanelItemKey(undefined));
    } else {
      return content;
    }
  };

  const computedPinned =
    navbar?.appMenu?.menuItems && navbar.appMenu.menuItems.length > 0
      ? pinned
      : false;

  React.useEffect(() => {
    const handleViewportResize = () => {
      const underMinWidth = checkIsMinimizedMode();
      const wasUnder = wasUnderMinWidth.current;

      if (!wasUnder && underMinWidth) {
        // Přechod DO minimalizovaného módu – zapamatovat a zrušit připnutí
        wasUnderMinWidth.current = true;
        setIsMinimizedMode(true);
        setPinned((prevPinned) => {
          pinnedBeforeMinimize.current = prevPinned ?? false;
          if (prevPinned) {
            localStorage.setItem('pinnedPanel', JSON.stringify(false));
            return false;
          }
          return prevPinned;
        });
      } else if (wasUnder && !underMinWidth) {
        // Přechod Z minimalizovaného módu – obnovit předchozí stav připnutí
        wasUnderMinWidth.current = false;
        setIsMinimizedMode(false);
        if (pinnedBeforeMinimize.current) {
          localStorage.setItem('pinnedPanel', JSON.stringify(true));
          setPinned(true);
        }
        pinnedBeforeMinimize.current = undefined;
      }
    };

    // Počáteční kontrola při mountu – pokud je viewport pod minimální šířkou, odepnout
    if (wasUnderMinWidth.current) {
      setPinned((prevPinned) => {
        pinnedBeforeMinimize.current = prevPinned ?? false;
        if (prevPinned) {
          localStorage.setItem('pinnedPanel', JSON.stringify(false));
          return false;
        }
        return prevPinned;
      });
    }

    window.addEventListener('resize', handleViewportResize);
    return () => window.removeEventListener('resize', handleViewportResize);
  }, []);

  return (
    <div
      className={classNames(className, Classes.APP_LAYOUT_WRAPPER)}
      style={style}
    >
      <div>
        <Navbar
          onClick={navbar?.appMenu?.onClick}
          icon={navbar?.icon}
          pinned={computedPinned}
          menuItems={navbar?.appMenu?.menuItems ? navbar.appMenu.menuItems : []}
          togglePin={togglePin}
          currentKey={navbar?.appMenu?.currentKey}
          userMenu={navbar?.userMenu}
          panelWidth={navbar?.appMenu?.panelWidth}
          isMinimizedMode={isMinimizedMode}
        >
          <div className={classNames(Classes.TITLE_H3, Classes.NAVBAR_BRAND)}>
            {navbar?.brand}
            {appConfig.testEnvironment && <TestEnvironmentBar />}
          </div>
          {!isMobile && (
            <div className={Classes.NAVBAR_CENTER}>{navbar?.center}</div>
          )}
          {sidePanel && sidePanel.items.length > 0 && (
            <div className={Classes.NAVBAR_RIGHT}>
              <div
                className={Classes.NAVBAR_RIGHT_CONTENT}
                ref={sidePanelButtonsRef}
              >
                {sidePanel.items.map((item) => {
                  const tempItem = {
                    ...item,
                    type:
                      !item.action && !item.onClick
                        ? SidePanelItemActionEnum.PIN_PANEL
                        : item.action,
                  };
                  const button = (
                    <NavbarButton
                      icon={item.icon}
                      key={item.key}
                      onClick={
                        tempItem.action === SidePanelItemActionEnum.ON_CLICK
                          ? tempItem.onClick
                          : () => handleSidePanelItemChange(tempItem)
                      }
                      style={
                        selectedSidePanelItem?.key !== tempItem.key
                          ? 'link'
                          : 'default'
                      }
                      title={tempItem.label}
                    />
                  );
                  return tempItem.count ? (
                    <BadgeCount
                      count={tempItem.count}
                      intent={tempItem?.countIntent ?? 'danger'}
                    >
                      {button}
                    </BadgeCount>
                  ) : (
                    button
                  );
                })}
              </div>
            </div>
          )}
        </Navbar>
      </div>
      <div
        id={'containerVyskaId'}
        className={Classes.APP_LAYOUT_BASE_NAVBAR_CONTENT}
      >
        {computedPinned && navbar?.appMenu && (
          <div
            className={classNames(
              Classes.NAVBAR_MENU_CONTAINER,
              Classes.INVERSE_SCROLL_CONTAINER
            )}
          >
            <NavMenu
              onClick={navbar?.appMenu?.onClick}
              currentKey={navbar?.appMenu?.currentKey}
              menuItems={navbar.appMenu.menuItems}
              panelWidth={navbar?.appMenu?.panelWidth}
              scrollWrapStyle={{
                minWidth:
                  (navbar?.appMenu?.panelWidth ?? NAVBAR_DEFAULT_WIDTH) - 14, // Respektování gutteru
              }}
            />
          </div>
        )}
        <div className={Classes.APP_LAYOUT_BASE_APP_CONTENT}>{children}</div>
        {!isMobile && (
          <SidePanelContentRenderer
            selectedItem={selectedSidePanelItem}
            popUpRef={popUpRef}
            sidePanelButtonsRef={sidePanelButtonsRef}
            sidePanel={sidePanel}
            onClose={() => setSelectedSidePanelItemKey(undefined)}
            renderContent={renderSelectedItemContent}
          />
        )}
      </div>
    </div>
  );
};
